import { useEffect } from 'react';
import { Link, useLocation } from 'react-router';
import { ArrowRight, Check } from 'lucide-react';
import { Section } from '../components/section';
import { MODULES, type ModuleDef } from '../lib/modules';

export function FeaturesPage() {
  const loc = useLocation();

  // Smooth-scroll to anchored section if hash present.
  useEffect(() => {
    if (!loc.hash) return;
    const id = loc.hash.slice(1);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loc.hash]);

  return (
    <>
      <header className="mx-auto max-w-6xl px-6 pb-12 pt-20 md:pt-24">
        <div className="max-w-3xl">
          <p
            className="text-[11px] font-semibold uppercase"
            style={{ color: 'var(--color-accent-600)', letterSpacing: '0.10em' }}
          >
            Features
          </p>
          <h1
            className="mt-3"
            style={{
              fontSize: 'clamp(2rem, 4vw, 3.25rem)',
              lineHeight: 1.05,
              fontWeight: 700,
              letterSpacing: '-0.032em',
            }}
          >
            One platform, five disciplines, zero copy-paste.
          </h1>
          <p className="mt-5 text-[16px] leading-relaxed" style={{ color: 'var(--fg-mid)' }}>
            Each module is a fully-featured product on its own. Together, they share
            students, the ledger and the academic calendar — so the office team never
            re-keys the same row twice.
          </p>
        </div>

        <nav
          aria-label="Module navigation"
          className="mt-10 flex flex-wrap gap-2 border-t pt-6"
          style={{ borderColor: 'var(--border-hair)' }}
        >
          {MODULES.map((m) => (
            <a
              key={m.id}
              href={`#${m.id}`}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12.5px] font-medium transition-colors"
              style={{
                background: 'var(--bg-surface)',
                color: 'var(--fg-mid)',
                border: '1px solid var(--border-line)',
              }}
            >
              <m.icon className="h-3.5 w-3.5" />
              {m.name}
            </a>
          ))}
        </nav>
      </header>

      {MODULES.map((m, i) => (
        <ModuleSection key={m.id} module={m} reverse={i % 2 === 1} tone={i % 2 === 1 ? 'sunken' : 'canvas'} />
      ))}

      <Section align="center">
        <div
          className="mx-auto max-w-3xl rounded-xl p-10 text-center"
          style={{
            border: '1px solid var(--border-line)',
            background: 'var(--bg-surface)',
            boxShadow: 'var(--elev-2)',
          }}
        >
          <h2
            style={{
              fontSize: '1.75rem',
              fontWeight: 600,
              letterSpacing: '-0.028em',
            }}
          >
            See it on your own data.
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-[15px]" style={{ color: 'var(--fg-mid)' }}>
            We&apos;ll import a sample of your existing records and walk you through every
            module in 30 minutes.
          </p>
          <Link to="/contact" className="ed-btn-primary mt-6 inline-flex items-center gap-2">
            Get a demo
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </Section>
    </>
  );
}

function ModuleSection({
  module,
  reverse,
  tone,
}: {
  module: ModuleDef;
  reverse: boolean;
  tone: 'canvas' | 'sunken';
}) {
  return (
    <Section id={module.id} tone={tone}>
      <div className="grid items-center gap-10 md:grid-cols-2 md:gap-16">
        <div className={reverse ? 'md:order-2' : 'md:order-1'}>
          <span
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg"
            style={{
              background: 'var(--semantic-success-bg)',
              color: 'var(--semantic-success)',
            }}
          >
            <module.icon className="h-5 w-5" />
          </span>
          <p
            className="mt-4 text-[11px] font-semibold uppercase"
            style={{ color: 'var(--color-accent-600)', letterSpacing: '0.10em' }}
          >
            {module.name}
          </p>
          <h2
            className="mt-2"
            style={{
              fontSize: 'clamp(1.5rem, 2.6vw, 2.25rem)',
              lineHeight: 1.15,
              fontWeight: 600,
              letterSpacing: '-0.028em',
            }}
          >
            {module.tagline}
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed" style={{ color: 'var(--fg-mid)' }}>
            {module.description}
          </p>

          <ul className="mt-6 space-y-2.5">
            {module.bullets.map((b) => (
              <li
                key={b}
                className="flex items-start gap-2.5 text-[13.5px]"
                style={{ color: 'var(--fg-high)' }}
              >
                <Check
                  className="mt-[3px] h-3.5 w-3.5 flex-shrink-0"
                  style={{ color: 'var(--color-accent-500)' }}
                />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className={reverse ? 'md:order-1' : 'md:order-2'}>
          <ProductFrame module={module} />
        </div>
      </div>
    </Section>
  );
}

function ProductFrame({ module }: { module: ModuleDef }) {
  const Icon = module.icon;
  return (
    <div
      className="relative aspect-[4/3] overflow-hidden rounded-xl"
      style={{
        background:
          'linear-gradient(180deg, var(--bg-surface) 0%, var(--bg-sunken) 100%)',
        border: '1px solid var(--border-line)',
        boxShadow: 'var(--elev-2)',
      }}
    >
      {/* Grid backdrop */}
      <div
        aria-hidden
        className="ed-grid-backdrop absolute inset-0"
        style={{ opacity: 0.45 }}
      />
      {/* Content */}
      <div className="relative flex h-full flex-col items-center justify-center px-8">
        <span
          className="inline-flex h-14 w-14 items-center justify-center rounded-xl"
          style={{
            background: 'var(--bg-surface)',
            color: 'var(--color-accent-600)',
            border: '1px solid var(--border-line)',
            boxShadow: 'var(--elev-2)',
          }}
        >
          <Icon className="h-6 w-6" />
        </span>
        <p
          className="mt-4 text-center font-mono text-[10px] uppercase"
          style={{ color: 'var(--fg-low)', letterSpacing: '0.12em' }}
        >
          / dashboard / {module.id}
        </p>
        <div
          className="mt-6 grid w-full max-w-xs grid-cols-3 gap-2"
          aria-hidden
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-1.5 rounded-full"
              style={{
                background:
                  i % 3 === 0
                    ? 'var(--color-accent-500)'
                    : 'var(--border-line)',
                opacity: i % 3 === 0 ? 1 : 0.6,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
